import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useCallback } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import type { BranchData } from "../components/Workflow";
import { resolveDefaultPlanMode, savePlanMode } from "../lib/planMode";
import { navigate, routes } from "../lib/urls";
import { trackOptimisticBranch } from "./useBranchServerReady";
import { useQuery } from "./useQuery";

export function useForkAsBranch(spaceId: string, workflowId: string) {
  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const planModeBranchDefault = resolveDefaultPlanMode(preferences, "branch");

  return useCallback(
    (branch: BranchData, viewedCommitId: string | null) => {
      const sourceCommitId = viewedCommitId ?? branch.headCommitId;
      if (!sourceCommitId) return;

      const branchId = nanoid(12);
      const chatId = nanoid(12);
      const viewedCheckpoint = viewedCommitId
        ? branch.commits.find((c) => c.id === viewedCommitId)
        : null;

      let name: string;
      if (branch.name === "main") {
        const checkpoints = branch.commits.filter((c) => c.message);
        const versionIndex = viewedCheckpoint
          ? checkpoints.indexOf(viewedCheckpoint) + 1
          : checkpoints.length;
        name = `Fork of ${versionIndex > 0 ? `v${versionIndex}` : "main"}`;
      } else {
        name = `Fork of ${branch.name}`;
      }

      const result = z.mutate(
        sharedMutators.createBranch({
          branchId,
          commitId: nanoid(12),
          chatId,
          workflowId,
          name,
          sourceCommitId,
        })
      );
      trackOptimisticBranch(branchId, result.server, () =>
        navigate(
          routes.space.workflow,
          {
            spaceId,
            workflowId,
            branchId: branch.id,
            commitId: viewedCommitId ?? "",
          },
          { replace: true }
        )
      );
      savePlanMode(chatId, planModeBranchDefault);
      navigate(routes.space.workflow, {
        spaceId,
        workflowId,
        branchId,
        commitId: "",
      });
    },
    [planModeBranchDefault, spaceId, workflowId, z]
  );
}
